/**
 * D1 / R2 database-name and service-catalog identifier constants -- the concrete NAME strings the Cloudflare account resolves.
 * Distinct from `@federa/const/database` (migration-directory registry) and `@federa/const/bindings` (wrangler binding names).
 * Every map pins its identifier grammar as a template-literal `satisfies` clause: the shared affix routes the value, and a member dropping it fails silently at runtime.
 */

import type { DeployEnv } from '@federa/types/wrangler-deploy-env';

/** A database name carrying its deploy env as a `-dev` / `-prod` suffix: the grammar both name maps below pin. */
type EnvSuffixedDatabaseName = `${string}-${DeployEnv}`;

export const DATABASE_NAMES = {
	KAIZEN_DEV: 'kaizen-dev',
	KAIZEN_PROD: 'kaizen-prod',
	ACTIVITIES_DEV: 'activities-dev',
	ACTIVITIES_PROD: 'activities-prod',
	FUNCTIONS_DEV: 'functions-dev',
	FUNCTIONS_PROD: 'functions-prod',
	KNOWLEDGE_DEV: 'knowledge-dev',
	KNOWLEDGE_PROD: 'knowledge-prod',
} as const satisfies Record<string, EnvSuffixedDatabaseName>;

export const DATABASE_NAMES_EXTENDED = {
	GATEWAY_DEV: 'storage-gateway-dev',
	GATEWAY_PROD: 'storage-gateway-prod',
	CONVERSATIONS_DEV: 'federa-dev',
	CONVERSATIONS_PROD: 'federa-prod',
	GROUPS_DEV: 'groups-dev',
	GROUPS_PROD: 'groups-prod',
	// The phone-canonical contact-book database (contacts phase 1). DEV provisioned 2026-08-24 (wnam), PROD provisioned
	// 2026-09-04 (enam) -- both ids live in CF_RESOURCE_IDS.D1.CONTACTS_DB.
	CONTACTS_DEV: 'contacts-dev',
	CONTACTS_PROD: 'contacts-prod',
} as const satisfies Record<string, EnvSuffixedDatabaseName>;

/** Firebase dev project ID (distinct from DATABASE_NAMES_EXTENDED.GATEWAY_DEV after DB rename). */
export const FIREBASE_PROJECT_ID_DEV = DATABASE_NAMES_EXTENDED.CONVERSATIONS_DEV;

/** Every name is namespace-qualified -- an unqualified table resolves against no namespace and fails at R2 SQL, not at build time -- so the grammar is pinned as a template-literal type. */
export const R2_TABLES = {
	WF_RUNS: 'default.wf_runs',
	WF_NODE_RESULTS: 'default.wf_node_results',
} as const satisfies Record<string, `default.${string}`>;

/** Stable service catalog IDs (must match migration 0032). These are FK values, so a member missing the `svc-` prefix silently joins zero rows; the template-literal type makes that a compile error. */
export const SERVICE_IDS = {
	SMARTLISTING_FULLSERVICE: 'svc-smartlisting-fullservice',
	SMARTLISTING_LIVE: 'svc-smartlisting-live',
	SMARTLISTING_DIY: 'svc-smartlisting-diy',
	BUYERS_AGENT: 'svc-buyers-agent',
} as const satisfies Record<string, `svc-${string}`>;
